import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { TourStyledProductTypeInfoModalResult } from 'components/Tours/TourStyledProductTypeInfoModal'
import TourStyledProductTypeInfoModal from 'components/Tours/TourStyledProductTypeInfoModal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import { EmptyArray } from 'lib/array/arrayUtils'
import { isTourProductTypeStyled } from 'lib/tours/tourUtils'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import useTourStyleBenefits from 'hooks/TourV2/useTourStyleBenefits'

const BenefitTile = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template: "content image" auto / minmax(0, 1fr) ${rem(68)};

  ${mediaQueryUp.tablet} {
    grid-template: "image content" auto / ${rem(68)} minmax(0, 1fr);
  }
`

const BenefitContent = styled(VerticalSpacer)`
  grid-area: content;
`

const BenefitImage = styled(Image)`
  grid-area: image;
`

interface Props {
  offer: App.Tours.TourV2Offer
  onCheckAvailability: () => void
}

function TourStyleKeyBenefitsSection({ offer, onCheckAvailability }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { tourStylesBenefits, tourStylesGroupBenefit, privateTourBenefit, tourStyledHeading, privateTourStyledHeading } = useTourStyleBenefits()

  const checkAvailabilityText = intl.formatMessage({
    id: 'Qa4GkXriBbrNNPPWO6R3',
    defaultMessage: 'Check availability',
    description: 'Button text to check tour availability on the tour offer detail page',
  })

  const openTourStyleInfoModal = useCallback<MouseEventHandler<HTMLButtonElement>>(async(event) => {
    event.preventDefault()
    if (!isTourProductTypeStyled(offer.productType)) return
    const result = await showModal<TourStyledProductTypeInfoModalResult>(<TourStyledProductTypeInfoModal
      primaryActionText={checkAvailabilityText}
      productType={offer.productType}
    />)
    if (result === 'primary-action') {
      onCheckAvailability()
    }
  }, [onCheckAvailability, showModal, offer, checkAvailabilityText])

  const [styleHeading, styleBenefits] = useMemo(() => {
    if (!isTourProductTypeStyled(offer.productType)) return ['', EmptyArray] as const
    const heading = offer.privateJourney ? privateTourStyledHeading[offer.productType] : tourStyledHeading[offer.productType]
    const benefits = [...tourStylesBenefits[offer.productType]]
    if (offer.privateJourney) benefits.push(privateTourBenefit)
    else benefits.push(tourStylesGroupBenefit[offer.productType])
    return [heading, benefits] as const
  }, [offer, tourStylesBenefits, tourStylesGroupBenefit, privateTourBenefit, tourStyledHeading, privateTourStyledHeading])

  return <VerticalSpacer gap={24}>
    <HeadingTextBlock
      variant="heading4"
      endIcon={offer.productType !== 'custom_tour' && <Clickable onClick={openTourStyleInfoModal}><LineInfoCircleIcon /></Clickable>}
    >
      {styleHeading}
    </HeadingTextBlock>
    <Group
      direction="vertical"
      gap={20}
      tabletGap={24}
      tabletDirection="horizontal"
      tabletHorizontalAlign="stretch"
    >
      {styleBenefits.map((benefit, index) => <BenefitTile key={index}>
        <BenefitContent gap={4}>
          <Heading variant="heading6" colour="neutral-one">{benefit.title}</Heading>
          <BodyText variant="medium" colour="neutral-two">{benefit.description}</BodyText>
        </BenefitContent>
        <BenefitImage image={benefit.image} width={68} aspectRatio="1" dpr={2} />
      </BenefitTile>)}
    </Group>
  </VerticalSpacer>
}

export default TourStyleKeyBenefitsSection
